import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import RichText from '../rich-text/rich-text';
import vars from '@variables';
import {
  ArrowUpIconAtom,
  ArrowDownIconAtom,
} from '@atoms/icon-atoms/icon-atoms';
import { AccordionProps } from './accordion.types';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H3Atom } from '@atoms/text-atoms/text-atoms';

const Accordion: React.FC<AccordionProps> = ({
  title = 'Click to expand',
  richTextContent,
  richTextTooltipData,
  itemId,
  isOpen,
  setIsOpen,
  className,
  urlHasAnchorId,
}) => {
  const panel = useRef<HTMLDivElement>(null);
  const accordionRef = useRef<HTMLDivElement>(null);
  const h3TextRef = useRef<HTMLHeadingElement>(null);

  /*First accordion open by default when there is an anchor ID in the URL*/
  useEffect(() => {
    if (urlHasAnchorId && parseInt(itemId.split('-')[1]) === 0) {
      setIsOpen(itemId);
    }
  }, [itemId, setIsOpen, urlHasAnchorId]);

  /* Update panel styling on isOpen state change  */
  useEffect(() => {
    if (panel.current) {
      let panelStyle = panel.current.style;
      if (isOpen === itemId) {
        panelStyle.maxHeight = `${panel.current.scrollHeight}px`;
        panelStyle.opacity = '1';
        panelStyle.overflow = 'visible';
      } else {
        panelStyle.opacity = '0';
        panelStyle.maxHeight = '0px';
        panelStyle.overflow = 'hidden';
      }
    }
  }, [isOpen, itemId]);

  /* Update panel height on window resize. */
  useEffect(() => {
    const updateMaxHeight = () => {
      if (panel.current) {
        panel.current.style.maxHeight = `${panel.current.scrollHeight}px`;
      }
    };

    isOpen === itemId
      ? window.addEventListener('resize', updateMaxHeight)
      : window.removeEventListener('resize', updateMaxHeight);

    return () => window.removeEventListener('resize', updateMaxHeight);
  }, [isOpen, itemId]);

  // deal with down arrow svg clicks sending correct data to GTM
  const handleSvgClick = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>
  ) => {
    const accordion = accordionRef.current;
    const h3Text = h3TextRef.current?.textContent;

    if (!accordion) {
      console.error('Accordion container not found');
      return;
    }

    if (!h3Text) {
      console.error('Accordion H3 element not found');
      return;
    }

    if (window?.dataLayer !== undefined) {
      window.dataLayer.push({
        event: 'accordion-item',
        label: h3Text,
        ClickText: h3Text,
      });
    } else {
      console.error('window.dataLayer is not available');
      return;
    }
  };

  return (
    <div className={className} data-testid="accordion">
      <div
        ref={accordionRef}
        className="accordion"
        data-testid="accordion-header"
        onClick={() =>
          isOpen === itemId ? setIsOpen(null) : setIsOpen(itemId)
        }
        onKeyDown={() =>
          isOpen === itemId ? setIsOpen(null) : setIsOpen(itemId)
        }
        role="button"
        tabIndex={0}
        data-gtm-id={isOpen === itemId ? null : GTM_ID.ACCORDION_ITEM}
      >
        <H3Atom ref={h3TextRef}>{title}</H3Atom>
        {isOpen === itemId ? (
          <ArrowUpIconAtom />
        ) : (
          <ArrowDownIconAtom onClick={handleSvgClick} />
        )}
      </div>
      <div className="panel" ref={panel}>
        <RichText
          content={richTextContent}
          tooltipData={richTextTooltipData}
          isAccordionOpen={isOpen === itemId}
        />
      </div>
    </div>
  );
};

const StyledAccordion = styled(Accordion)`
  width: 100%;
  border-top: 1px solid ${vars.transparentCoreGrey};

  &:last-of-type {
    padding-bottom: 0;
  }

  .accordion {
    display: flex;
    user-select: none;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    line-height: 4rem;
  }
  ${H3Atom} {
    display: inline-block;
    width: 90%;
    margin: 0;
    font-weight: ${vars.fontWeightMedium};
    ${vars.fontSize4}
  }
  ${ArrowUpIconAtom}, ${ArrowDownIconAtom} {
    min-width: 2rem;
  }
  .panel {
    max-height: 0px;
    opacity: 0;
    overflow: 'hidden';
    transition: 0.12s ease-out;
    ${RichText} {
      overflow: hidden;
      background: none;
      line-height: 1.6;

      p {
        margin: 0.8rem 0 1.6rem 0;
      }

      .call-to-action-block-content {
        grid-column: col1-start/col4-end;

        @media screen and (min-width: ${vars.breakPointTablet}) {
          grid-column: col1-start/col8-end;
        }

        @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
          grid-column: col1-start/col12-end;
        }
      }
    }
  }
  @media screen and (min-width: ${vars.breakPointTenColumn}) {
    img {
      width: 40%;
      margin: 0;
    }
  }
`;

export default StyledAccordion;
